import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, UserPlus, Volume2, VolumeX, ArrowRight, Compass, Share2, Check } from "lucide-react";
import { getCallingCard } from "../lib/adventureApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function downloadVcard(d) {
  const vcf = [
    "BEGIN:VCARD", "VERSION:3.0",
    `FN:${d.raj_name || "Responsible Adventures"}`,
    `ORG:Responsible Adventures`,
    d.raj_role ? `TITLE:${d.raj_role}` : "",
    d.raj_phone ? `TEL;TYPE=CELL:${d.raj_phone}` : "",
    d.raj_email ? `EMAIL;TYPE=INTERNET:${d.raj_email}` : "",
    "URL:https://responsibleadventures.com",
    "END:VCARD",
  ].filter(Boolean).join("\n");
  const blob = new Blob([vcf], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${(d.raj_name || "responsible-adventures").replace(/\s+/g, "-").toLowerCase()}.vcf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function CallingCard() {
  const [d, setD] = useState(null);
  const [muted, setMuted] = useState(true);
  const [shared, setShared] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => { getCallingCard().then(setD).catch(() => setD({})); }, []);
  useEffect(() => { document.body.style.background = "#0d0d0d"; return () => { document.body.style.background = ""; }; }, []);

  if (!d) return <div className="min-h-screen bg-ink" />;

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const nextMuted = !v.muted;
    v.muted = nextMuted;
    if (!nextMuted) { v.volume = 1; v.play().catch(() => {}); }
    setMuted(nextMuted);
  };

  const share = async () => {
    const url = window.location.href;
    const payload = { title: d.title || "Responsible Adventures", text: d.tagline || "Beyond the ordinary.", url };
    if (navigator.share) {
      try { await navigator.share(payload); } catch (_) {}
    } else {
      try { await navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 2000); } catch (_) {}
    }
  };

  const posterUrl = d.has_poster ? `${BACKEND}/api/calling-card/poster` : undefined;

  return (
    <div className="relative min-h-[100dvh] w-full overflow-x-hidden bg-ink text-white" data-testid="calling-card">
      {/* Blurred ambient background (never the main view, so cropping here is fine) */}
      <div className="fixed inset-0 z-0">
        {posterUrl ? (
          <img src={posterUrl} alt="" className="w-full h-full object-cover blur-2xl scale-110 opacity-50" />
        ) : (
          <div className="w-full h-full bg-[radial-gradient(ellipse_at_top,#2a2520,#0d0d0d_70%)]" />
        )}
        <div className="absolute inset-0 bg-ink/80" />
      </div>

      {/* Top controls */}
      <div className="fixed top-5 right-5 z-30 flex items-center gap-2">
        <button onClick={share} data-testid="calling-card-share" aria-label="Share this intro"
          className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors">
          {shared ? <Check className="w-5 h-5 text-moss" /> : <Share2 className="w-5 h-5" />}
        </button>
        {d.has_video && (
          <button onClick={toggleSound} data-testid="calling-card-sound" aria-label="Toggle sound"
            className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        )}
      </div>

      {/* Foreground */}
      <div className="relative z-10 min-h-[100dvh] flex flex-col items-center justify-center max-w-xl mx-auto w-full px-5 py-16 gap-7">
        {/* Full, uncropped video in a framed player */}
        {d.has_video ? (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            className="relative w-full rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
            <video ref={videoRef} src={`${BACKEND}/api/calling-card/video`} poster={posterUrl} autoPlay muted={muted} loop playsInline
              onClick={toggleSound}
              className="w-full max-h-[60vh] object-contain bg-black cursor-pointer" data-testid="calling-card-video" />
            {muted && (
              <button onClick={toggleSound} data-testid="calling-card-unmute-overlay" aria-label="Tap for sound"
                className="absolute inset-0 flex items-end justify-center pb-5 bg-gradient-to-t from-black/40 to-transparent group">
                <span className="flex items-center gap-2 bg-white/90 text-ink px-4 h-10 rounded-full text-sm font-semibold shadow-lg group-hover:bg-white transition-colors">
                  <VolumeX className="w-4 h-4" /> Tap for sound
                </span>
              </button>
            )}
          </motion.div>
        ) : posterUrl ? (
          <img src={posterUrl} alt={d.title} className="w-full rounded-xl max-h-[60vh] object-contain shadow-2xl ring-1 ring-white/10" />
        ) : null}

        {/* Brand + CTAs */}
        <div className="w-full">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta mb-2">Responsible Adventures</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight leading-[1.04]">{d.title}</motion.h1>
          {d.tagline && (
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
              className="mt-3 text-white/80 leading-relaxed">{d.tagline}</motion.p>
          )}

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-6 grid grid-cols-2 gap-3">
            {d.raj_phone && (
              <a href={`tel:${d.raj_phone.replace(/\s+/g, "")}`} data-testid="calling-card-call"
                className="flex items-center justify-center gap-2 bg-white text-ink py-3.5 rounded-sm font-semibold hover:bg-white/90 transition-colors">
                <Phone className="w-4 h-4" /> Call Raj
              </a>
            )}
            {d.raj_whatsapp && (
              <a href={`https://wa.me/${d.raj_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="calling-card-whatsapp"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 rounded-sm font-semibold hover:brightness-95 transition-all">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            <button onClick={() => downloadVcard(d)} data-testid="calling-card-vcard"
              className="col-span-2 flex items-center justify-center gap-2 bg-terracotta text-white py-3.5 rounded-sm font-semibold hover:bg-terracotta-hover transition-colors">
              <UserPlus className="w-4 h-4" /> Save {d.raj_name ? d.raj_name.split(" ")[0] + "'s" : "our"} contact
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.46 }}
            className="mt-5 flex items-center justify-between text-white/60 text-sm">
            <span className="font-mono text-[11px]">{d.raj_name} · {d.raj_role}</span>
            <Link to="/" data-testid="calling-card-explore" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Compass className="w-4 h-4" /> Explore journeys <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
